import { useState, useEffect, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import { getClass, findWeaponByName, getWeapon, getWeaponAbility, formatAttackBonus, isAnyWeaponToken, getWeaponsForToken, formatWeaponTokenLabel } from '@character-creator/data';
import type { Weapon } from '@character-creator/data';
import { useCharacterCreationStore } from '../../../stores/characterCreationStore';
import { useCharacterBenefits } from '../../../hooks/useCharacterBenefits';
import { ANY_WEAPON_PREFERENCES } from '../../../utils/characterDefaults';

// D&D 5e damage types
const DAMAGE_TYPES = [
  'bludgeoning',
  'piercing',
  'slashing',
  'acid',
  'cold',
  'fire',
  'force',
  'lightning',
  'necrotic',
  'poison',
  'psychic',
  'radiant',
  'thunder',
] as const;

// Format equipment item ID to display name
function formatEquipmentName(id: string): string {
  return id
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Check if an equipment item is a weapon (or a weapon category token)
function isWeapon(itemId: string): boolean {
  return findWeaponByName(itemId) != null || isAnyWeaponToken(itemId);
}

interface EquipmentChoice {
  choose: number;
  from: string[][];
}

type StartingEquipmentItem = string | EquipmentChoice;

function isEquipmentChoice(item: StartingEquipmentItem): item is EquipmentChoice {
  return typeof item !== 'string' && 'choose' in item;
}

// Parse old format damage strings that include damage type
function parseDamageString(damage: string): { damage: string; type: string | null } {
  const lowerDamage = damage.toLowerCase();
  for (const type of DAMAGE_TYPES) {
    const typePattern = new RegExp(`\\s+(${type}|${type.slice(0, 4)}|${type.slice(0, 5)})\\b`, 'i');
    if (typePattern.test(lowerDamage)) {
      const cleanDamage = damage.replace(typePattern, '').trim();
      return { damage: cleanDamage, type };
    }
  }
  return { damage, type: null };
}

export function CombatStep() {
  const { draft, addAttack, updateAttack, removeAttack, setAttacks, setEquipmentChoices, setWeaponChoiceResolution } = useCharacterCreationStore();
  const benefits = useCharacterBenefits();
  const [weaponSelections, setWeaponSelections] = useState<Record<number, number>>({});

  const selectedClass = draft.classId ? getClass(draft.classId) : null;

  // Safe access to benefits with fallbacks
  const strMod = benefits?.modifiers?.strength ?? 0;
  const dexMod = benefits?.modifiers?.dexterity ?? 0;
  const profBonus = benefits?.proficiencyBonus ?? 2;
  const speed = benefits?.speed ?? 30;
  const hitPoints = benefits?.hitPoints ?? 1;

  // Get weapon choices from class equipment (only weapon items)
  const { weaponChoices, fixedWeapons, fixedAnyTokens } = useMemo(() => {
    const choices: { index: number; choice: EquipmentChoice }[] = [];
    const fixed: string[] = [];
    const anyTokens: { index: number; token: string }[] = [];

    if (selectedClass) {
      selectedClass.startingEquipment.forEach((item, index) => {
        if (isEquipmentChoice(item)) {
          // Check if this choice contains any weapon options
          const hasWeaponOptions = item.from.some((optionGroup) =>
            optionGroup.some((itemId) => isWeapon(itemId))
          );
          if (hasWeaponOptions) {
            choices.push({ index, choice: item });
          }
        } else if (isAnyWeaponToken(item)) {
          anyTokens.push({ index, token: item });
        } else if (findWeaponByName(item) != null) {
          fixed.push(item);
        }
      });
    }

    return { weaponChoices: choices, fixedWeapons: fixed, fixedAnyTokens: anyTokens };
  }, [selectedClass]);

  // Initialize weapon selections from store or defaults
  useEffect(() => {
    if (draft.equipmentSelections) {
      setWeaponSelections(draft.equipmentSelections);
    } else {
      // Default to first option for each choice
      const defaults: Record<number, number> = {};
      weaponChoices.forEach(({ index }) => {
        defaults[index] = 0;
      });
      setWeaponSelections(defaults);
    }
  }, [draft.equipmentSelections, weaponChoices]);

  // Handle weapon selection change
  const handleWeaponSelectionChange = (choiceIndex: number, optionIndex: number) => {
    const newSelections = { ...weaponSelections, [choiceIndex]: optionIndex };
    setWeaponSelections(newSelections);
    setEquipmentChoices(newSelections);
  };

  // Initialize weapon resolutions from class preferences when class changes
  useEffect(() => {
    if (!draft.classId) return;
    const prefs = ANY_WEAPON_PREFERENCES[draft.classId] ?? {};

    // Collect all any-* tokens from choices and fixed items that need defaults
    const tokensNeedingDefaults: { key: string; token: string }[] = [];

    // From choices: scan each option group for any-* tokens
    weaponChoices.forEach(({ index, choice }) => {
      choice.from.forEach((optionGroup) => {
        optionGroup.forEach((itemId, itemIdx) => {
          if (isAnyWeaponToken(itemId)) {
            tokensNeedingDefaults.push({ key: `${index}-${itemIdx}`, token: itemId });
          }
        });
      });
    });

    // From fixed items
    fixedAnyTokens.forEach(({ index, token }) => {
      tokensNeedingDefaults.push({ key: `fixed-${index}`, token });
    });

    // Set defaults only for keys that don't already have a resolution
    tokensNeedingDefaults.forEach(({ key, token }) => {
      if (!draft.weaponChoiceResolutions[key]) {
        const defaultWeaponId = prefs[token];
        if (defaultWeaponId) {
          setWeaponChoiceResolution(key, defaultWeaponId);
        } else {
          // Fall back to first weapon in the matching list
          const matching = getWeaponsForToken(token);
          if (matching.length > 0) {
            setWeaponChoiceResolution(key, matching[0].id);
          }
        }
      }
    });
  }, [draft.classId, weaponChoices, fixedAnyTokens, draft.weaponChoiceResolutions, setWeaponChoiceResolution]);

  // Migrate old attacks that have damage type embedded in damage string
  // Intentionally runs once on mount to migrate legacy data format
  useEffect(() => {
    if (draft.attacks && draft.attacks.length > 0) {
      let needsMigration = false;
      const migratedAttacks = draft.attacks.map(attack => {
        if (attack.damageType && DAMAGE_TYPES.includes(attack.damageType as typeof DAMAGE_TYPES[number])) {
          return attack;
        }
        const parsed = parseDamageString(attack.damage);
        if (parsed.type) {
          needsMigration = true;
          return { ...attack, damage: parsed.damage, damageType: parsed.type };
        }
        return attack;
      });
      if (needsMigration) {
        setAttacks(migratedAttacks);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve an item ID to a weapon, handling any-* tokens via weaponChoiceResolutions
  const resolveWeaponItem = useCallback((itemId: string, resolutionKey: string): Weapon | undefined => {
    if (isAnyWeaponToken(itemId)) {
      const resolvedId = draft.weaponChoiceResolutions[resolutionKey];
      if (resolvedId) return getWeapon(resolvedId);
      // Fall back to class preferences
      const prefs = draft.classId ? ANY_WEAPON_PREFERENCES[draft.classId] : undefined;
      const prefId = prefs?.[itemId];
      if (prefId) return getWeapon(prefId);
      return undefined;
    }
    return findWeaponByName(itemId);
  }, [draft.weaponChoiceResolutions, draft.classId]);

  // Generate suggested attacks based on equipment
  const generateSuggestedAttacks = useCallback(() => {
    try {
      const foundWeapons: { weapon: Weapon }[] = [];

      if (selectedClass) {
        selectedClass.startingEquipment.forEach((item, eqIndex) => {
          if (typeof item === 'string') {
            const weapon = resolveWeaponItem(item, `fixed-${eqIndex}`);
            if (weapon) foundWeapons.push({ weapon });
          } else if ('choose' in item && 'from' in item) {
            const selectedOptionIndex = draft.equipmentSelections?.[eqIndex] ?? 0;
            const selectedOption = item.from[selectedOptionIndex];
            if (selectedOption) {
              selectedOption.forEach((optItem, itemIdx) => {
                const weapon = resolveWeaponItem(optItem, `${eqIndex}-${itemIdx}`);
                if (weapon) foundWeapons.push({ weapon });
              });
            }
          }
        });
      }

      const uniqueWeapons = foundWeapons.filter(
        (w, i, arr) => arr.findIndex(x => x.weapon.id === w.weapon.id) === i
      );

      const attacks = uniqueWeapons.slice(0, 3).map(({ weapon }) => {
        const { modifier } = getWeaponAbility(weapon, strMod, dexMod);
        const attackBonus = modifier + profBonus;
        const modStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;

        return {
          name: weapon.name,
          bonus: formatAttackBonus(attackBonus),
          damage: weapon.damage === '0' ? 'Special' : `${weapon.damage}${modStr}`,
          damageType: weapon.damageType,
        };
      });

      return attacks;
    } catch (e) {
      console.error('Error generating suggested attacks:', e);
      return [];
    }
  }, [selectedClass, draft.equipmentSelections, resolveWeaponItem, strMod, dexMod, profBonus]);

  // Auto-populate attacks from starting weapons when empty
  // Note: draft.attacks intentionally excluded from deps to avoid infinite loop after setAttacks
  useEffect(() => {
    // Only auto-populate if:
    // 1. We have a class selected
    // 2. Attacks array is empty (user hasn't customized)
    // 3. We have suggestions to add
    if (selectedClass && (!draft.attacks || draft.attacks.length === 0)) {
      const suggested = generateSuggestedAttacks();
      if (suggested.length > 0) {
        setAttacks(suggested);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, draft.equipmentSelections, generateSuggestedAttacks, setAttacks]);

  const handleSuggestAttacks = () => {
    const suggested = generateSuggestedAttacks();
    if (suggested.length > 0) {
      setAttacks(suggested);
    }
  };

  const suggestedAttacks = generateSuggestedAttacks();
  const hasSuggestions = suggestedAttacks.length > 0;

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-bold text-text-primary mb-2">
          Weapons & Attacks
        </h2>
        <p className="text-text-secondary">
          Select your starting weapons and configure your weapon attacks.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Left Column - Weapon Selection & Attacks */}
        <div className="space-y-6">
          {/* Weapon Selection */}
          {selectedClass && (weaponChoices.length > 0 || fixedWeapons.length > 0 || fixedAnyTokens.length > 0) && (
            <div className="card p-6">
              <h3 className="font-semibold text-accent-gold mb-4">
                {selectedClass.name} Starting Weapons
              </h3>

              {/* Weapon Choices */}
              {weaponChoices.length > 0 && (
                <div className="space-y-4 mb-4">
                  {weaponChoices.map(({ index, choice }) => (
                    <WeaponChoiceSelector
                      key={index}
                      choiceIndex={index}
                      choice={choice}
                      selectedIndex={weaponSelections[index] ?? 0}
                      onChange={(optionIndex) => handleWeaponSelectionChange(index, optionIndex)}
                      weaponChoiceResolutions={draft.weaponChoiceResolutions}
                      onWeaponResolutionChange={setWeaponChoiceResolution}
                    />
                  ))}
                </div>
              )}

              {/* Fixed Weapons */}
              {(fixedWeapons.length > 0 || fixedAnyTokens.length > 0) && (
                <div>
                  <h4 className="text-sm font-medium text-text-muted mb-2">
                    Also included:
                  </h4>
                  <ul className="space-y-2 text-sm text-text-secondary">
                    {consolidateWeapons(fixedWeapons).map((item, i) => (
                      <li key={`fixed-${i}`} className="flex items-start gap-2">
                        <span className="text-accent-gold">•</span>
                        {item}
                      </li>
                    ))}
                    {fixedAnyTokens.map(({ index, token }) => (
                      <li key={`any-${index}`}>
                        <WeaponTokenPicker
                          token={token}
                          resolutionKey={`fixed-${index}`}
                          selectedWeaponId={draft.weaponChoiceResolutions[`fixed-${index}`] ?? ''}
                          onSelect={(weaponId) => setWeaponChoiceResolution(`fixed-${index}`, weaponId)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Weapon Attacks */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-accent-gold text-lg">
                Weapon Attacks
              </h3>
              {hasSuggestions && (
                <button
                  type="button"
                  onClick={handleSuggestAttacks}
                  className="text-sm text-accent-gold hover:text-accent-gold/80 transition-colors"
                >
                  Auto-fill from equipment
                </button>
              )}
            </div>
            <p className="text-sm text-text-muted mb-4">
              Add your weapon attacks. The PDF character sheet has space for up to 3 attacks.
              {hasSuggestions && (draft.attacks?.length ?? 0) === 0 && (
                <span className="block mt-1 text-accent-gold">
                  We found {suggestedAttacks.length} weapon{suggestedAttacks.length !== 1 ? 's' : ''} in your equipment!
                </span>
              )}
            </p>

            {/* Attack list */}
            <div className="space-y-4 mb-4">
              {(draft.attacks ?? []).map((attack, index) => (
                <div key={index} className="p-4 rounded-lg bg-background-secondary border border-border-default">
                  <div className="flex items-center justify-between mb-3">
                    <input
                      type="text"
                      value={attack.name}
                      onChange={(e) => updateAttack(index, { name: e.target.value })}
                      placeholder="Weapon name"
                      className="text-lg font-medium bg-transparent text-text-primary placeholder:text-text-muted focus:outline-none border-b border-transparent focus:border-accent-gold transition-colors flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeAttack(index)}
                      className="text-text-muted hover:text-error transition-colors p-1 ml-2"
                      aria-label="Remove attack"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Attack Bonus</label>
                      <input
                        type="text"
                        value={attack.bonus}
                        onChange={(e) => updateAttack(index, { bonus: e.target.value })}
                        placeholder="+5"
                        className="w-full px-3 py-2 rounded-lg border border-border-default bg-background-primary text-text-primary text-center font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold/50 focus:border-accent-gold"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Damage</label>
                      <input
                        type="text"
                        value={attack.damage}
                        onChange={(e) => updateAttack(index, { damage: e.target.value })}
                        placeholder="1d8+3"
                        className="w-full px-3 py-2 rounded-lg border border-border-default bg-background-primary text-text-primary text-center font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold/50 focus:border-accent-gold"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Type</label>
                      <select
                        value={attack.damageType || 'slashing'}
                        onChange={(e) => updateAttack(index, { damageType: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-border-default bg-background-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold/50 focus:border-accent-gold"
                      >
                        {DAMAGE_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add attack button */}
            {(draft.attacks?.length ?? 0) < 3 && (
              <button
                type="button"
                onClick={() => addAttack({ name: '', bonus: '', damage: '', damageType: 'slashing' })}
                className="w-full py-3 rounded-lg border border-dashed border-border-default text-text-muted hover:border-accent-gold hover:text-accent-gold transition-colors"
              >
                + Add Attack
              </button>
            )}

            {(draft.attacks?.length ?? 0) === 0 && !hasSuggestions && (
              <p className="text-sm text-text-muted text-center py-4 italic">
                No weapons in your equipment. Add attacks manually or check the Inventory sub-tab for equipment.
              </p>
            )}
          </div>
        </div>

        {/* Right Column - Combat Stats */}
        <div className="space-y-6">
          {/* Combat Stats */}
          <div className="card p-6">
            <h3 className="font-semibold text-text-primary mb-4">Combat Stats</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 rounded-lg bg-background-secondary">
                <div className="text-2xl font-bold text-text-primary">
                  {10 + dexMod}
                </div>
                <div className="text-xs text-text-muted mt-1">Base AC</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-background-secondary">
                <div className="text-2xl font-bold text-text-primary">
                  {dexMod >= 0 ? '+' : ''}{dexMod}
                </div>
                <div className="text-xs text-text-muted mt-1">Initiative</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-background-secondary">
                <div className="text-2xl font-bold text-text-primary">
                  {speed}
                </div>
                <div className="text-xs text-text-muted mt-1">Speed</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-background-secondary">
                <div className="text-2xl font-bold text-accent-gold">
                  {hitPoints}
                </div>
                <div className="text-xs text-text-muted mt-1">Hit Points</div>
              </div>
            </div>
            <p className="text-xs text-text-muted mt-4 text-center">
              AC shown is base (10 + DEX). Armor will modify this.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface WeaponChoiceSelectorProps {
  choiceIndex: number;
  choice: EquipmentChoice;
  selectedIndex: number;
  onChange: (index: number) => void;
  weaponChoiceResolutions: Record<string, string>;
  onWeaponResolutionChange: (key: string, weaponId: string) => void;
}

function WeaponChoiceSelector({
  choiceIndex,
  choice,
  selectedIndex,
  onChange,
  weaponChoiceResolutions,
  onWeaponResolutionChange,
}: WeaponChoiceSelectorProps) {
  // Filter options to only show weapons (or mixed weapon/non-weapon options)
  const weaponOptions = choice.from
    .map((option, index) => ({ option, originalIndex: index }))
    .filter(({ option }) => option.some((itemId) => isWeapon(itemId)));

  if (weaponOptions.length === 0) {
    return null;
  }

  return (
    <div className="border border-border-default rounded-lg p-3">
      <p className="text-sm text-text-muted mb-2">
        Choose {choice.choose === 1 ? 'one' : choice.choose}:
      </p>
      <div className="space-y-2">
        {weaponOptions.map(({ option, originalIndex }) => {
          const weaponItems = option.filter((itemId) => isWeapon(itemId));
          const isSelected = selectedIndex === originalIndex;

          // Build label: use formatWeaponTokenLabel for any-* tokens, formatEquipmentName for real weapons
          const label = weaponItems
            .map((id) => isAnyWeaponToken(id) ? formatWeaponTokenLabel(id) : formatEquipmentName(id))
            .join(' and ');

          return (
            <div key={originalIndex}>
              <button
                type="button"
                onClick={() => onChange(originalIndex)}
                className={clsx(
                  'w-full text-left p-3 rounded-lg border text-sm transition-all duration-200',
                  isSelected
                    ? 'bg-accent-gold/10 border-accent-gold text-text-primary'
                    : 'bg-background-secondary border-transparent hover:border-text-muted text-text-secondary'
                )}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={clsx(
                      'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                      isSelected ? 'border-accent-gold' : 'border-text-muted'
                    )}
                  >
                    {isSelected && (
                      <div className="w-2 h-2 rounded-full bg-accent-gold" />
                    )}
                  </div>
                  <span>{label}</span>
                </div>
              </button>

              {/* Show weapon picker dropdowns for any-* tokens when this option is selected */}
              {isSelected && weaponItems.some((id) => isAnyWeaponToken(id)) && (
                <div className="ml-6 mt-2 space-y-2">
                  {weaponItems.map((itemId, itemIdx) =>
                    isAnyWeaponToken(itemId) ? (
                      <WeaponTokenPicker
                        key={`${choiceIndex}-${itemIdx}`}
                        token={itemId}
                        resolutionKey={`${choiceIndex}-${itemIdx}`}
                        selectedWeaponId={weaponChoiceResolutions[`${choiceIndex}-${itemIdx}`] ?? ''}
                        onSelect={(weaponId) => onWeaponResolutionChange(`${choiceIndex}-${itemIdx}`, weaponId)}
                      />
                    ) : null
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Dropdown picker for selecting a specific weapon from a category token (e.g., "any-martial-melee")
 */
interface WeaponTokenPickerProps {
  token: string;
  resolutionKey: string;
  selectedWeaponId: string;
  onSelect: (weaponId: string) => void;
}

function WeaponTokenPicker({ token, selectedWeaponId, onSelect }: WeaponTokenPickerProps) {
  const matchingWeapons = useMemo(() => getWeaponsForToken(token), [token]);

  return (
    <div>
      <label className="text-xs text-text-muted block mb-1">
        {formatWeaponTokenLabel(token)}
      </label>
      <select
        value={selectedWeaponId}
        onChange={(e) => onSelect(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-border-default bg-background-secondary text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-gold/50 focus:border-accent-gold"
      >
        {!selectedWeaponId && <option value="">Select a weapon...</option>}
        {matchingWeapons.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name} — {w.damage} {w.damageType}{w.properties.length > 0 ? ` (${w.properties.join(', ')})` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

// Consolidate duplicate weapon items (e.g., 4x javelin -> "Javelin (4)")
function consolidateWeapons(items: string[]): string[] {
  const counts: Record<string, number> = {};
  for (const item of items) {
    counts[item] = (counts[item] || 0) + 1;
  }

  return Object.entries(counts).map(([item, count]) => {
    const name = formatEquipmentName(item);
    return count > 1 ? `${name} (${count})` : name;
  });
}
